import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import Inventory2 from '@mui/icons-material/Inventory2';

import { TEXT_COLORS } from '../utils/colors';

interface ItemIconProps {
  icon: string;
  iconPanel?: string;
  name: string;
  size?: number;
  count?: number;
  hasCapsule?: boolean;
  /**
   * Картинка не загрузилась (файла нет). Нужно, чтобы показать вместо неё что-то осмысленное —
   * например, имя текстуры. Без обработчика поведение прежнее.
   */
  onError?: () => void;
}

const ANIMATION_FRAMES = [
  '/items/usecount_effect.png',
  '/items/usecount_effect_01.png',
  '/items/usecount_effect_02.png',
  '/items/usecount_effect_03.png',
  '/items/usecount_effect_04.png',
  '/items/usecount_effect_05.png',
  '/items/usecount_effect_06.png',
  '/items/usecount_effect_07.png',
  '/items/usecount_effect_08.png',
  '/items/usecount_effect_09.png',
];

// Lazy loading: preload only when first animated icon is rendered
let preloadedFrames: HTMLImageElement[] | null = null;
let globalFrameIndex = 0;
let animationInterval: NodeJS.Timeout | null = null;
let animatedIconCount = 0; // Reference counter for cleanup

const preloadAnimationFrames = () => {
  if (preloadedFrames) return; // Already loaded

  preloadedFrames = [];

  // Preload images - browser will cache them in disk cache automatically
  ANIMATION_FRAMES.forEach((src) => {
    const img = new Image();
    img.src = src; // Browser will cache this with HTTP cache headers
    preloadedFrames!.push(img);
  });
};

const startAnimationInterval = () => {
  animatedIconCount++;
  if (animationInterval) return; // Already running

  animationInterval = setInterval(() => {
    globalFrameIndex = (globalFrameIndex + 1) % 10;
  }, 100);
};

const stopAnimationInterval = () => {
  animatedIconCount--;
  if (animatedIconCount <= 0 && animationInterval) {
    clearInterval(animationInterval);
    animationInterval = null;
    animatedIconCount = 0;
  }
};

const getIconName = (icon: string) => {
  if (!icon) return null;
  const trimmed = icon.trim();
  if (trimmed === '' || trimmed === '-') return null;
  return trimmed;
};

const getPanelName = (panel?: string) => {
  if (!panel) return null;
  const trimmed = panel.trim();
  if (trimmed === '' || trimmed === '-') return null;
  return trimmed;
};

// Слои иконки — обычные элементы со стилем, а не `Box sx`: иконка стоит в каждой строке больших
// таблиц (игровые данные), и styled-обёртка на каждый слой была заметной долей цены отрисовки
// при быстрой прокрутке. Вид тот же. GPU-свойства сюда НЕ добавлять — docs/ui-patterns.md §7.
const ICON_LAYER_STYLE: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  imageRendering: 'pixelated',
  zIndex: 1,
};

const PANEL_LAYER_STYLE: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  imageRendering: 'pixelated',
  pointerEvents: 'none',
  objectFit: 'contain',
  zIndex: 2,
};

const ItemIcon: React.FC<ItemIconProps> = React.memo(({ icon, iconPanel, name, size = 64, count, hasCapsule, onError }) => {
  const [, forceUpdate] = useState(0);
  const isAnimated = iconPanel && getPanelName(iconPanel) === 'usecount_effect';

  useEffect(() => {
    if (!isAnimated) return;

    // Lazy load animation frames only when first animated icon appears
    preloadAnimationFrames();
    startAnimationInterval();

    const interval = setInterval(() => {
      forceUpdate(prev => prev + 1);
    }, 100);

    return () => {
      clearInterval(interval);
      stopAnimationInterval(); // Clean up global interval when no animated icons remain
    };
  }, [isAnimated]);

  const iconName = getIconName(icon);
  const panelName = getPanelName(iconPanel);

  const frameUrl = isAnimated
    ? ANIMATION_FRAMES[globalFrameIndex]
    : (panelName ? `/items/${panelName}.png` : null);

  // Use no_img.png as fallback if icon is invalid
  const iconSrc = iconName ? `/items/${iconName}.png` : '/items/no_img.png';

  return (
    <div
      style={{
        position: 'relative',
        width: size,
        height: size,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        isolation: 'isolate',
      }}
    >
      {/* Item icon - bottom layer */}
      <img src={iconSrc} alt={name} loading="lazy" onError={onError} style={ICON_LAYER_STYLE} />

      {/* Icon panel (border) - top layer, above icon */}
      {frameUrl && <img src={frameUrl} alt="border" style={PANEL_LAYER_STYLE} />}

      {/* Count badge */}
      {count != null && count > 1 && (
        <Box
          sx={{
            position: 'absolute',
            bottom: -4,
            right: -4,
            zIndex: 3,
            bgcolor: '#000000',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '4px',
            px: 0.5,
            py: 0.125,
          }}
        >
          <Typography sx={{ fontSize: '0.6rem', color: TEXT_COLORS.PRIMARY, fontWeight: 600, lineHeight: 1 }}>
            x{count}
          </Typography>
        </Box>
      )}

      {/* Capsule badge */}
      {hasCapsule && (
        <Box
          sx={{
            position: 'absolute',
            top: -4,
            right: -4,
            zIndex: 3,
            bgcolor: '#000000',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '4px',
            width: 14,
            height: 14,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Inventory2 sx={{ fontSize: '0.5rem', color: TEXT_COLORS.MUTED }} />
        </Box>
      )}
    </div>
  );
});

ItemIcon.displayName = 'ItemIcon';

export default ItemIcon;
